import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
} from '@mui/material';

import type { CleanupWizardDataState } from '../hooks/useCleanupWizardData';

import type { CleanupWizardProps } from './CleanupWizard';
import { CleanupWizard } from './CleanupWizard';

export interface CleanupWizardDialogProps {
  open: boolean;
  onClose: () => void;
  state: CleanupWizardDataState;
  onApply?: CleanupWizardProps['onApply'];
  onStepChange?: CleanupWizardProps['onStepChange'];
}

/**
 * Fullscreen Dialog around the cleanup wizard (GDN-2295). Pure presentation:
 * the container owns the data state, tracking, and what Apply means. The
 * wizard brings its own full-height shell (fixed header, sticky footer), so
 * the dialog contributes only the fullscreen surface and a close affordance.
 */
export const CleanupWizardDialog: React.FC<CleanupWizardDialogProps> = ({
  open,
  onClose,
  state,
  onApply,
  onStepChange,
}) => (
  <Dialog open={open} onClose={onClose} fullScreen>
    <IconButton
      aria-label="Close cleanup wizard"
      onClick={onClose}
      sx={{ position: 'absolute', top: 8, right: 12, zIndex: 1 }}
    >
      <CloseIcon />
    </IconButton>
    {state.status === 'ready' ? (
      <CleanupWizard
        data={state.data}
        onApply={onApply}
        onStepChange={onStepChange}
      />
    ) : (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        {state.status === 'loading' ? (
          <CircularProgress aria-label="Loading cleanup preview" />
        ) : (
          <Alert severity="error">
            The cleanup preview could not be loaded. Close this window and try
            again.
          </Alert>
        )}
      </Box>
    )}
  </Dialog>
);
